"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePowerUsageEnergySavings = exports.computeEnergyWeekComparison = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
const energyRate_1 = require("../../utils/energyRate");
const energyWeekComparison_1 = require("../../utils/energyWeekComparison");
var energyWeekComparison_2 = require("../../utils/energyWeekComparison");
Object.defineProperty(exports, "computeEnergyWeekComparison", { enumerable: true, get: function () { return energyWeekComparison_2.computeEnergyWeekComparison; } });
const DEFAULT_DAYS = 30;
/**
 * Transforms SensorUsageRollup data into DailyEnergyCost format
 * Handles hourly rollup data by aggregating into daily totals
 */
const transformPowerDataToEnergyCosts = (powerData, energyRatePerKwh) => {
    // Group data by date to handle hourly rollups
    const dataByDate = {};
    // First pass: group all hourly data by date
    powerData.forEach((hourlyData) => {
        const date = hourlyData.date;
        if (!dataByDate[date]) {
            dataByDate[date] = [];
        }
        dataByDate[date].push(hourlyData);
    });
    // Second pass: calculate daily totals from hourly data
    return Object.entries(dataByDate).map(([date, hourlyRecords]) => {
        // Calculate the total energy for each hour and sum them up
        let dailyEnergyKwh = 0;
        hourlyRecords.forEach((hourData) => {
            // Add this hour's energy to the daily total
            dailyEnergyKwh += (0, energyWeekComparison_1.getRollupHourlyKwh)(hourData);
        });
        // Calculate cost for the day
        const costForDay = dailyEnergyKwh * energyRatePerKwh;
        return {
            label: date,
            value: Number(costForDay.toFixed(2)),
        };
    });
};
/**
 * Hook that provides energy savings data based on SensorUsageRollup records
 * @param params - Parameters for fetching and calculating energy savings
 * @returns Energy savings data with cost calculations
 */
const usePowerUsageEnergySavings = ({ sensorId, sensorIds, days = DEFAULT_DAYS, hubConfig, enabled = true, }) => {
    const [data, setData] = (0, react_1.useState)(null);
    const [weekComparison, setWeekComparison] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    // Support both sensorId (single) and sensorIds (multi) — merge into one list
    const effectiveIds = (0, react_1.useMemo)(() => {
        if (sensorIds && sensorIds.length > 0)
            return sensorIds.filter(Boolean);
        if (sensorId)
            return [sensorId];
        return [];
    }, [sensorId, sensorIds?.join(',')]);
    const effectiveEnergyRate = (0, react_1.useMemo)(() => (0, energyRate_1.getRatePerKwh)(hubConfig), [
        hubConfig ? JSON.stringify(hubConfig) : null,
    ]);
    // Calculate date range
    const endDate = (0, react_1.useMemo)(() => new Date().toISOString().split('T')[0], // Today in YYYY-MM-DD
    []);
    const startDate = (0, react_1.useMemo)(() => {
        const date = new Date();
        date.setDate(date.getDate() - days);
        return date.toISOString().split('T')[0]; // N days ago in YYYY-MM-DD
    }, [days]);
    const fetchRollupForSensor = async (id) => {
        const allData = [];
        let nextToken;
        do {
            const result = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorUsageRollup,
                variables: {
                    sensorId: id,
                    startDate,
                    endDate,
                    nextToken,
                },
            });
            const response = result?.data?.getSensorUsageRollup;
            const pageData = response?.items;
            if (pageData?.length > 0) {
                allData.push(...pageData);
            }
            nextToken = response?.nextToken;
        } while (nextToken);
        return allData;
    };
    const fetchPowerUsageData = async () => {
        if (!enabled || effectiveIds.length === 0) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // Fetch rollups for all sensors in parallel, then merge by date.
            // Per-sensor catch: one flaky sensor must not blank the whole chart.
            const allResults = await Promise.all(effectiveIds.map(async (id) => {
                try {
                    return await fetchRollupForSensor(id);
                }
                catch (err) {
                    console.error(`Error fetching usage rollup for sensor ${id}:`, err);
                    return null;
                }
            }));
            const successes = allResults.filter((r) => r !== null);
            if (successes.length === 0 && effectiveIds.length > 0) {
                throw new Error('Failed to fetch usage rollups for all power monitors');
            }
            const mergedData = successes.flat();
            if (mergedData.length > 0) {
                const historicalData = transformPowerDataToEnergyCosts(mergedData, effectiveEnergyRate);
                setData(historicalData);
                setWeekComparison((0, energyWeekComparison_1.computeEnergyWeekComparison)(mergedData, effectiveEnergyRate));
            }
            else {
                setData([]);
                setWeekComparison(null);
            }
        }
        catch (err) {
            console.error('Error fetching power usage data for energy savings:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch power usage data for energy savings');
            setData(null);
            setWeekComparison(null);
        }
        finally {
            setLoading(false);
        }
    };
    const refetch = async () => {
        await fetchPowerUsageData();
    };
    // Store previous values to prevent unnecessary refetches
    const prevSensorIdsRef = (0, react_1.useRef)(null);
    const prevEnergyRateRef = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(() => {
        const idsKey = effectiveIds.join(',');
        const sensorChanged = prevSensorIdsRef.current !== idsKey;
        const rateChanged = prevEnergyRateRef.current !== effectiveEnergyRate;
        if (sensorChanged || rateChanged || !data) {
            prevSensorIdsRef.current = idsKey;
            prevEnergyRateRef.current = effectiveEnergyRate;
            fetchPowerUsageData();
        }
    }, [effectiveIds.join(','), startDate, endDate, enabled, effectiveEnergyRate]);
    return {
        data,
        weekComparison,
        loading,
        error,
        refetch,
    };
};
exports.usePowerUsageEnergySavings = usePowerUsageEnergySavings;
